import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { Card, CardContent } from '../ui/card';
import { standards } from '../../data/standards/standardsData';
import { relatedStandards } from '../../data/courseRoutes';

const containerVariants = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.1 } },
};

const cardVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5 } },
};

/**
 * Displays a 3-column grid of related ISO standard cards.
 *
 * Props:
 *   currentSlug  — slug of the standard currently being viewed (e.g. 'iso-9001')
 *   heading      — optional section heading override
 *   slugs        — optional explicit array of slugs to show (overrides relatedStandards map)
 */
export const RelatedCoursesSection = ({
  currentSlug,
  heading = 'Related ISO Training Courses',
  slugs,
}) => {
  const relatedSlugs = slugs || relatedStandards[currentSlug] || [];
  const relatedItems = relatedSlugs
    .map((slug) => standards[slug])
    .filter(Boolean);

  if (relatedItems.length === 0) return null;

  return (
    <section className="py-16 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-10"
        >
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-3">
            {heading}
          </h2>
          <p className="text-gray-600 max-w-2xl mx-auto">
            Expand your expertise with these complementary ISO management system courses.
          </p>
        </motion.div>

        <motion.div
          className="grid md:grid-cols-2 lg:grid-cols-3 gap-6"
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
        >
          {relatedItems.map((std) => {
            const Icon = std.icon;
            return (
              <motion.div key={std.slug} variants={cardVariants}>
                <Card className={`h-full border-2 ${std.color.border} hover:shadow-lg transition-all group`}>
                  <CardContent className="p-6 flex flex-col h-full">
                    <div className="flex items-start gap-4 mb-4">
                      <div className={`p-3 rounded-xl bg-gradient-to-br ${std.color.gradient} flex-shrink-0`}>
                        <Icon className="h-6 w-6 text-white" />
                      </div>
                      <div>
                        <p className={`text-xs font-bold uppercase tracking-wider ${std.color.text} mb-1`}>
                          {std.code}
                        </p>
                        <h3 className="text-base font-bold text-gray-900 leading-snug">
                          {std.name}
                        </h3>
                      </div>
                    </div>

                    <p className="text-sm text-gray-600 leading-relaxed flex-1 mb-5">
                      {std.shortDescription}
                    </p>

                    <Link
                      to={`/training/${std.slug}`}
                      className={`inline-flex items-center gap-1.5 text-sm font-semibold ${std.color.text} ${std.color.hover} rounded-lg px-3 py-2 -mx-1 transition-colors group-hover:gap-2.5`}
                    >
                      View Courses
                      <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                    </Link>
                  </CardContent>
                </Card>
              </motion.div>
            );
          })}
        </motion.div>
      </div>
    </section>
  );
};
